/**
 * Locale provider — single source of truth for the active language
 * across the SPA.
 *
 * Resolution order on mount:
 *   1. `localStorage['phage.locale']` (set by switcher)
 *   2. `profile.preferred_locale` once `useProfile()` resolves
 *   3. `'uk'` (Ukrainian by default; a stored `ru-*` locale falls back to it)
 *
 * Falls back to Ukrainian for any missing key — keeps the UI working
 * while translations are being filled in.
 */
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useProfile } from '../hooks/use-profile.js';
import { LOCALES, type Locale, MESSAGES, type MessageKey } from './messages.js';

type Vars = Record<string, string | number>;

interface Ctx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (k: MessageKey) => string;
  /** `t` with `{name}` placeholders filled. */
  tf: (k: MessageKey, vars: Vars) => string;
  /** Plural form `${base}.one|few|many|other` for `n`, `{n}` and `vars` filled. */
  tp: (base: string, n: number, vars?: Vars) => string;
}

const LocaleCtx = createContext<Ctx | null>(null);

const STORAGE_KEY = 'phage.locale';

function readStored(): Locale | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return (LOCALES as readonly string[]).includes(raw ?? '') ? (raw as Locale) : null;
  } catch {
    return null;
  }
}

function fromProfileLocale(raw: string | undefined): Locale | null {
  if (!raw) return null;
  // Preferred-locale comes as `uk-UA`, `pl-PL`, etc. Take the language tag.
  const tag = raw.toLowerCase().split('-')[0] ?? '';
  if (tag === 'en') return 'en';
  if (tag === 'uk') return 'uk';
  return null;
}

export function fill(text: string, vars: Vars): string {
  return text.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStored() ?? 'uk');
  const { data: profile } = useProfile();

  // First time profile arrives, adopt its preferred_locale unless the
  // user already chose explicitly.
  useEffect(() => {
    if (readStored()) return;
    const fromProfile = fromProfileLocale(profile?.preferred_locale);
    if (fromProfile && fromProfile !== locale) {
      setLocaleState(fromProfile);
    }
  }, [profile?.preferred_locale, locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage blocked: the choice lasts for this page only.
    }
  }, []);

  const t = useCallback(
    (key: MessageKey): string => {
      const dict: Partial<Record<MessageKey, string>> = MESSAGES[locale];
      return dict[key] ?? MESSAGES.uk[key] ?? key;
    },
    [locale],
  );

  const tf = useCallback((key: MessageKey, vars: Vars) => fill(t(key), vars), [t]);

  const tp = useCallback(
    (base: string, n: number, vars: Vars = {}) => {
      const rule = new Intl.PluralRules(locale === 'uk' ? 'uk-UA' : 'en-GB').select(n);
      const dict: Partial<Record<string, string>> = MESSAGES[locale];
      const text =
        dict[`${base}.${rule}`] ?? dict[`${base}.other`] ?? MESSAGES.uk[`${base}.other` as MessageKey];
      return fill(text ?? base, { n, ...vars });
    },
    [locale],
  );

  const value = useMemo<Ctx>(() => ({ locale, setLocale, t, tf, tp }), [locale, setLocale, t, tf, tp]);
  return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

export function useLocale(): Ctx {
  const v = useContext(LocaleCtx);
  if (!v) throw new Error('useLocale must be used within LocaleProvider');
  return v;
}

/** Shorthand — most callers only need `t`. */
export function useT(): (k: MessageKey) => string {
  return useLocale().t;
}
